// ============================================================================
// DateField — një fushë date që e kupton njeriun.
//
// PROBLEMI: tre lista rënëse (ditë / muaj / vit) janë të ngadalta — për të
// shkruar një datëlindje duhen tre klikime dhe një rrëshqitje e gjatë deri te
// viti 1927. Dhe një fushë e thjeshtë teksti e detyron njeriun të gjejë vetë
// formatin e "duhur".
//
// ZGJIDHJA: shkruaj lirshëm — "29061984", "29.06.1984", "29/6/84", "1984-06-29"
// — dhe fusha e rregullon vetë ndërsa shkruan. RENDITJA (ditë-muaj apo
// muaj-ditë apo vit i pari) merret nga RAJONI I PAJISJES, jo e ngulitur; shih
// `dateOrder()` te datetime.js. Kush do kalendarin, klikon ikonën 📅 dhe hapet
// zgjedhësi i vetë sistemit (aty mund të shkruhet edhe viti direkt).
//
// SIGURIA E TË DHËNAVE: jashtë kthehet gjithmonë "YYYY-MM-DD" — pra ajo që
// ruhet në bazë s'varet kurrë nga rajoni. Data e pamundur (31 shkurt) nuk
// ruhet: fusha e shënon me kuq dhe vlera e mëparshme mbetet e paprekur.
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtDate, parseLooseDate, maskDateInput, datePlaceholder } from "./datetime";

export default function DateField({
  value,
  onChange,
  inputStyle = {},
  disabled = false,
  className = "",
  ariaLabel,
}) {
  const { t } = useTranslation();
  const show = (v) => (v ? fmtDate(v, { fallback: "" }) : "");
  const [text, setText] = useState(() => show(value));
  const [bad, setBad] = useState(false);
  const lastValue = useRef(value);

  // Ndryshim nga jashtë (redaktim i një personi tjetër, pastrim i formularit).
  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
      setText(show(value));
      setBad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (iso) => {
    lastValue.current = iso;
    onChange(iso);
  };

  const handleText = (raw) => {
    const masked = maskDateInput(raw);
    setText(masked);
    const digits = masked.replace(/[^0-9]/g, "");
    if (!digits) { setBad(false); if (value) commit(""); return; }
    // Ruhet vetëm kur data është e plotë — që gjysma e shkruar ("29.06.19")
    // të mos ruhet si vit 2019 dhe pastaj të mbishkruhet.
    if (digits.length === 8) {
      const iso = parseLooseDate(masked);
      setBad(!iso);
      if (iso) commit(iso);
    } else {
      setBad(false);
    }
  };

  // Në dalje nga fusha pranohen edhe format e shkurtra: "1984" (vetëm viti,
  // plotësohet me 1 janar, si më parë) dhe "29.06.84".
  const handleBlur = () => {
    const digits = text.replace(/[^0-9]/g, "");
    if (!digits) { setBad(false); if (value) commit(""); return; }
    const iso = parseLooseDate(text);
    if (iso) { setBad(false); commit(iso); setText(show(iso)); }
    else setBad(true);
  };

  const borderBad = bad ? { borderColor: "#B3261E", outlineColor: "#B3261E" } : null;

  return (
    <div className={className}>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={ariaLabel || t("Date")}
          disabled={disabled}
          value={text}
          placeholder={datePlaceholder()}
          onChange={(e) => handleText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => { if (e.key === "Enter") handleBlur(); }}
          style={{ ...inputStyle, ...borderBad, width: "100%", boxSizing: "border-box", paddingRight: 38 }}
        />
        {/* Zgjedhësi i sistemit. Fusha `date` rri sipër ikonës me tejdukshmëri
            zero — kështu klikimi shkon direkt te kalendari i vetë pajisjes, pa
            varur nga `showPicker()`, që s'e kanë të gjithë shfletuesit. */}
        {/* IOS1 (13 shtator 2026) — te iPhone-i fusha `date` ka gjerësi të
            vetën të brendshme dhe e shpërfill `width:100%`: dilte e padukshme
            jashtë rrethit 26px dhe mbulonte gjysmën e fushës fqinje (gjinia
            hapte kalendarin). `overflow:hidden` e pret te rrethi; `minWidth:0`
            + `appearance:none` ia heqin gjerësinë e brendshme. */}
        <span style={{ position: "absolute", right: 6, width: 26, height: 26, overflow: "hidden", display: "inline-flex", alignItems: "center", justifyContent: "center", pointerEvents: disabled ? "none" : "auto" }}>
          <span aria-hidden="true" style={{ fontSize: 15, opacity: 0.65 }}>📅</span>
          <input
            type="date"
            tabIndex={-1}
            aria-label={t("Choose from the calendar")}
            disabled={disabled}
            value={value || ""}
            onChange={(e) => { const v = e.target.value; commit(v); setText(show(v)); setBad(false); }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", minWidth: 0, maxWidth: "100%", opacity: 0, cursor: "pointer", border: "none", padding: 0, margin: 0, WebkitAppearance: "none", appearance: "none" }}
          />
        </span>
      </div>
      {bad && (
        <div style={{ fontSize: 11, color: "#B3261E", marginTop: 3 }}>
          {t("Date not understood — write it like {{example}}", { example: datePlaceholder() })}
        </div>
      )}
    </div>
  );
}
